import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Card, Button, Field, Input, Select, UnitInput, Callout } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Scope 3 Categories based on GHG Protocol
// Scope 3 category functional status:
//   functional = backend calc + factor library are implemented for this category
//   placeholder categories show in the dropdown but are disabled until activated
// Non-React module scope: labels resolve from the stored language directly
// (same pattern as utils/auth.js). Option VALUES stay unchanged.
const L = (sq, en) => (localStorage.getItem('k360-lang') === 'en' ? en : sq);

const SCOPE3_CATEGORIES = [
  { value: 'business_travel',     label: L('Udhëtime Biznesore (Business Travel)', 'Business Travel'),                 functional: true  },
  { value: 'employee_commuting',  label: L('Udhëtimet e Punëtorëve (Employee Commuting)', 'Employee Commuting'),       functional: true  },
  { value: 'waste_disposal',      label: L('Menaxhimi i Mbetjeve (Waste Disposal)', 'Waste Disposal'),                 functional: false },
  { value: 'purchased_goods',     label: L('Mallra dhe Shërbime të Blera (Purchased Goods & Services)', 'Purchased Goods & Services'), functional: false },
  { value: 'upstream_transport',  label: L('Transporti Hyrës (Upstream Transportation)', 'Upstream Transportation'),   functional: false },
  { value: 'downstream_transport',label: L('Transporti Dalës (Downstream Transportation)', 'Downstream Transportation'), functional: false }
];

// Static fallbacks so dropdowns are never empty if API returns nothing
const SCOPE3_ACTIVITY_FALLBACKS = {
  business_travel: [
    { key: 'short_haul_economy', label: L('Fluturim Short-haul (Economy)', 'Short-haul Flight (Economy)') },
    { key: 'short_haul_business', label: L('Fluturim Short-haul (Business)', 'Short-haul Flight (Business)') },
    { key: 'medium_haul_economy', label: L('Fluturim Medium-haul (Economy)', 'Medium-haul Flight (Economy)') },
    { key: 'medium_haul_business', label: L('Fluturim Medium-haul (Business)', 'Medium-haul Flight (Business)') },
    { key: 'long_haul_economy', label: L('Fluturim Long-haul (Economy)', 'Long-haul Flight (Economy)') },
    { key: 'long_haul_business', label: L('Fluturim Long-haul (Business)', 'Long-haul Flight (Business)') },
    { key: 'rail_electric', label: L('Tren (Elektrik)', 'Rail (Electric)') },
    { key: 'rail_diesel', label: L('Tren (Dizel)', 'Rail (Diesel)') },
    { key: 'bus_city', label: L('Autobus (Qytet)', 'Bus (City)') },
    { key: 'bus_coach', label: L('Autobus (Interurban/Coach)', 'Bus (Intercity/Coach)') },
    { key: 'car_petrol', label: L('Makina (Benzinë) - km', 'Car (Petrol) - km') },
    { key: 'car_diesel', label: L('Makina (Naftë) - km', 'Car (Diesel) - km') },
    { key: 'car_hybrid', label: L('Makina (Hibrid) - km', 'Car (Hybrid) - km') },
    { key: 'car_ev', label: L('Makina (Elektrike) - km', 'Car (Electric) - km') },
    { key: 'petrol_fuel', label: L('Makina (Benzinë) - Litra', 'Car (Petrol) - Liters') },
    { key: 'diesel_fuel', label: L('Makina (Naftë) - Litra', 'Car (Diesel) - Liters') }
  ],
  employee_commuting: [
    { key: 'commute_walk', label: L('Këmbë', 'Walking') },
    { key: 'commute_bike', label: L('Biçikletë', 'Bicycle') },
    { key: 'commute_bus', label: L('Autobus (Pune)', 'Bus (Commute)') },
    { key: 'commute_rail', label: L('Tren/Tramvaj (Pune)', 'Rail/Tram (Commute)') },
    { key: 'commute_metro', label: 'Metro' },
    { key: 'commute_car_solo', label: L('Makina (Solo)', 'Car (Solo)') },
    { key: 'commute_car_carpool', label: L('Makina (Carpool)', 'Car (Carpool)') }
  ]
};

export const Scope3Page = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [formData, setFormData] = useState({
    buildingId: '',
    category: '',
    activityType: '',
    period: getCurrentMonth(),
    quantity: '',
    unit: 'km'
  });

  const [facilities, setFacilities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [calculated, setCalculated] = useState(null);
  const [, setSummary] = useState({ current: null, previous: null });
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [buildingValidationError, setBuildingValidationError] = useState('');
  // Verifiko → Ruaj → Vazhdo dialog
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [flowError, setFlowError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [, setSavedMsg] = useState('');
  const [ledgerKey, setLedgerKey] = useState(0);
  // Evidence upload (stored in the invoice register; shown in the verify dialog)
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docUrl, setDocUrl] = useState(null);
  useEffect(() => {
    if (!uploadedFile) { setDocUrl(null); return undefined; }
    const url = URL.createObjectURL(uploadedFile);
    setDocUrl(url);
    return () => URL.revokeObjectURL(url); // avoid leaking blob URLs across uploads
  }, [uploadedFile]);

  /**
   * Attach the supporting document. Takes the file directly now — the old version
   * built its own <input> and opened a picker, which would fire a second dialog
   * on top of the stepper's drop zone. The POST is unchanged.
   *
   * Register-only: scope3_records has no invoice_id column, so the document lands
   * in the register but cannot be linked to the record it evidences. Noted in the
   * handoff as a known gap, not introduced here.
   */
  const handleUploadDocument = async (picked) => {
    if (!picked) return;
    if (picked.size > 10 * 1024 * 1024) { setFlowError(t('entry.uploadTooBig')); return; }
    try {
      setUploading(true);
      setFlowError('');
      const fd = new FormData();
      fd.append('file', picked);
      fd.append('title', `Dokument Scope 3 - ${formData.category || 'udhëtime'} (${formData.period})`);
      fd.append('period', formData.period);
      fd.append('building_id', formData.buildingId);
      fd.append('scope', 'scope3');
      fd.append('status', 'completed');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      setUploadedFile(picked);
    } catch (err) {
      setFlowError(t('entry.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  const [totalEmployees, setTotalEmployees] = useState('');
  const [workDaysPerMonth, setWorkDaysPerMonth] = useState('22');
  const [commutingSurvey, setCommutingSurvey] = useState([
    { mode: 'walk', label: t('s3p.modeWalk'), percentage: '', avgDistance: '' },
    { mode: 'bike', label: t('s3p.modeBike'), percentage: '', avgDistance: '' },
    { mode: 'bus', label: t('s3p.modeBus'), percentage: '', avgDistance: '' },
    { mode: 'rail', label: t('s3p.modeRail'), percentage: '', avgDistance: '' },
    { mode: 'metro', label: 'Metro', percentage: '', avgDistance: '' },
    { mode: 'car_solo', label: t('s3p.modeCarSolo'), percentage: '', avgDistance: '' },
    { mode: 'car_carpool', label: t('s3p.modeCarCarpool'), percentage: '', avgDistance: '' }
  ]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => setFacilities(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch facilities:', err);
        setFacilities([]);
      });
  }, []);

  const fetchSummary = async (period) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/emissions/scope3/summary?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  useEffect(() => {
    if (formData.period) {
      fetchSummary(formData.period);
    }
  }, [formData.period]);

  // Fetch activity types when category changes
  useEffect(() => {
    if (!formData.category) {
      setActivityTypes([]);
      return;
    }

    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/fuels`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const scope3Fuels = data.filter(f => f.category === formData.category);
        if (scope3Fuels.length > 0) {
          setActivityTypes(scope3Fuels);
        } else {
          // fallback options so dropdown is never empty
          setActivityTypes(SCOPE3_ACTIVITY_FALLBACKS[formData.category] || []);
        }
      })
      .catch(err => console.error('Failed to fetch activity types:', err));
  }, [formData.category]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-switch unit to L when fuel-based car activity selected
      if (field === 'activityType') {
        if (value === 'petrol_fuel' || value === 'diesel_fuel') {
          newData.unit = 'L';
        } else if (prev.unit === 'L') {
          // Reset to km if switching away from fuel-based
          newData.unit = 'km';
        }
      }
      
      return newData;
    });
    setIsSaved(false);
    if (field === 'buildingId') {
      setBuildingValidationError('');
    }
  };

  // Update commuting survey row
  const handleSurveyChange = (index, field, value) => {
    setCommutingSurvey(prev => {
      const newSurvey = [...prev];
      newSurvey[index] = { ...newSurvey[index], [field]: value };
      return newSurvey;
    });
    setIsSaved(false);
    setCalculated(null);
  };

  // Calculate commuting survey emissions
  const handleCalculateCommutingSurvey = async () => {
    if (!totalEmployees || parseFloat(totalEmployees) <= 0) {
      alert(t('s3p.enterEmployees'));
      return;
    }

    // Build entries from survey data
    const entries = commutingSurvey
      .filter(row => row.percentage && row.avgDistance && parseFloat(row.percentage) > 0)
      .map(row => ({
        mode: row.mode,
        employeeCount: (parseFloat(row.percentage) / 100) * parseFloat(totalEmployees),
        avgDistanceKm: parseFloat(row.avgDistance),
        workDaysPerMonth: parseFloat(workDaysPerMonth) || 22
      }));

    if (entries.length === 0) {
      alert(t('s3p.fillOneMode'));
      return;
    }

    try {
      setCalculating(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE}/api/emissions/scope3/commuting-survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          entries,
          buildingId: formData.buildingId || null,
          period: formData.period
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Calculation failed');
      }

      const data = await response.json();
      setCalculated({
        ...data,
        category: 'employee_commuting',
        quantity: data.totalPkm,
        unit: 'pkm'
      });
      setIsSaved(false);
      // Verifiko → Ruaj → Vazhdo: open the dialog right after the calculation
      setFlowError('');
      setStep(2);
    } catch (err) {
      alert(t('s3p.error', { message: err.message }));
    } finally {
      setCalculating(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      const token = localStorage.getItem('token');

      // Call REAL API endpoint for calculation
      const response = await fetch(`${API_BASE}/api/emissions/scope3/calc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: formData.category,
          activityType: formData.activityType,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Calculation failed');
      }

      const data = await response.json();
      setCalculated(data);
      setIsSaved(false);
      // Verifiko → Ruaj → Vazhdo: open the dialog right after the calculation
      setFlowError('');
      setStep(2);
    } catch (err) {
      alert(t('s3p.error', { message: err.message }));
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    if (!calculated) return false;

    if (!formData.buildingId) {
      setBuildingValidationError(t('s3p.confirmBuilding'));
      return false;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Employee Commuting uses the survey endpoint with save:true to persist
      // one Scope3Record per non-zero-pkm mode (so per-mode breakdowns work).
      if (formData.category === 'employee_commuting') {
        const entries = commutingSurvey
          .filter(row => row.percentage && row.avgDistance && parseFloat(row.percentage) > 0)
          .map(row => ({
            mode: row.mode,
            employeeCount: (parseFloat(row.percentage) / 100) * parseFloat(totalEmployees),
            avgDistanceKm: parseFloat(row.avgDistance),
            workDaysPerMonth: parseFloat(workDaysPerMonth) || 22
          }));

        const response = await fetch(`${API_BASE}/api/emissions/scope3/commuting-survey`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            entries,
            buildingId: parseInt(formData.buildingId),
            period: formData.period,
            save: true
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Save failed');
        }

        const result = await response.json();
        console.log('✅ Scope 3 commuting saved:', result);

        setIsSaved(true);
        setBuildingValidationError('');
        await fetchSummary(formData.period);

        const created = (result.saved || []).filter(s => s.status === 'created').length;
        const skipped = (result.saved || []).filter(s => s.status === 'duplicate').length;
        setSavedMsg(t('s3p.commuteSaved', {
          created,
          dupes: skipped ? t('s3p.commuteSavedDupes', { skipped }) : '',
          period: formData.period
        }));
        return true;
      }

      // Non-commuting: existing per-record save path
      const response = await fetch(`${API_BASE}/api/emissions/scope3/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          buildingId: parseInt(formData.buildingId),
          category: formData.category,
          activityType: formData.activityType,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          emissions_tCO2e: calculated.emissions_tCO2e
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Save failed');
      }

      const result = await response.json();
      console.log('✅ Scope 3 saved successfully:', result);

      setIsSaved(true);
      setBuildingValidationError('');
      await fetchSummary(formData.period);
      setSavedMsg(t('s3p.saved', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period }));
      return true;
    } catch (err) {
      console.error('Save error:', err);
      setBuildingValidationError(t('s3p.error', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Dialog: save → coverage chips → success phase
  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await handleSave();
    if (!ok) {
      setFlowError(t('s3p.saveFailedCheckBuilding'));
      return;
    }
    try {
      setCoverage(await fetchCoverage(formData.period));
    } catch (e) {
      setCoverage(null);
    }
    setLedgerKey(k => k + 1);
  };

  const handleFlowAddAnother = () => {
    setStep(1);
    setCalculated(null);
    setIsSaved(false);
    setUploadedFile(null); // don't show the previous document with the next record
    setFormData(prev => ({ ...prev, activityType: '', quantity: '' }));
  };

  const handleFlowFinish = () => setStep(0);

  const isCalculateDisabled = !formData.category || !formData.activityType ||
    !formData.period || !formData.quantity || parseFloat(formData.quantity) <= 0 || calculating;

  // For commuting, hard-block save when survey % > 100 (still soft-warn under 100)
  const commutingTotalPct = commutingSurvey.reduce((sum, r) => sum + (parseFloat(r.percentage) || 0), 0);
  const isCommutingOverAllocated = formData.category === 'employee_commuting' && commutingTotalPct > 100;
  const isBusy = calculating || saving;

  const isCommuting = formData.category === 'employee_commuting';
  const selectedCategory = SCOPE3_CATEGORIES.find(c => c.value === formData.category);
  const categoryNotReady = !!selectedCategory && selectedCategory.functional === false;

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {isBusy && <Loader />}

          <PageHeader title={t('s3.title')} desc={t('s3.desc')}>
            <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>{t('entry.viewRecords')}</Button>
          </PageHeader>

          <EntryStepper
            scope={3}
            step={step}
            setStep={setStep}
            facilities={facilities}
            siteId={formData.buildingId}
            onSite={(e) => { handleInputChange('buildingId', e.target.value); setBuildingValidationError(''); }}
            siteError={buildingValidationError}
            /* scope3_records has no activity_purpose column. */
            purposes={[]}
            period={formData.period}
            mode={mode}
            setMode={setMode}
            ocrSupported={false}
            docLabel={t('s3.doc')}
            file={uploadedFile}
            onFile={handleUploadDocument}
            uploading={uploading}
            uploadNote={uploadedFile ? (
              <div style={{ marginTop: 10 }}><Callout tone="ok">{t('entry.uploadOk')}</Callout></div>
            ) : null}
            onCalculate={isCommuting ? handleCalculateCommutingSurvey : handleCalculate}
            calcDisabled={isCalculateDisabled || categoryNotReady}
            calcLabel={calculating ? t('entry.calculating') : t('entry.calc')}
            reviewRows={
              isCommuting
                ? [
                    { label: t('entry.building'), value: (facilities.find(f => String(f.id) === String(formData.buildingId)) || {}).name || '—' },
                    { label: t('s3.category'), value: t('s3.commuting') },
                    { label: t('s3.employees'), value: totalEmployees || '—' },
                    { label: t('s3.workDays'), value: workDaysPerMonth || '22' },
                    { label: t('entry.period'), value: formData.period },
                    ...(Array.isArray(calculated?.breakdown) ? calculated.breakdown.map(b => ({
                      label: b.mode.replace(/_/g, ' '),
                      value: `${Math.round(b.pkm).toLocaleString()} pkm — ${b.emissions_tCO2e.toFixed(3)} t`
                    })) : []),
                    { label: t('entry.document'), value: uploadedFile ? uploadedFile.name : t('entry.notAttached') }
                  ]
                : [
                    { label: t('entry.building'), value: (facilities.find(f => String(f.id) === String(formData.buildingId)) || {}).name || '—' },
                    { label: t('s3.category'), value: (selectedCategory || {}).label || formData.category || '—' },
                    { label: t('s3.activity'), value: (activityTypes.find(a => (a.value || a.key) === formData.activityType) || {}).label || formData.activityType || '—' },
                    { label: t('stationary.quantity'), value: `${formData.quantity} ${formData.unit}` },
                    { label: t('entry.period'), value: formData.period },
                    { label: t('entry.document'), value: uploadedFile ? uploadedFile.name : t('entry.notAttached') }
                  ]
            }
            result={{ value: calculated ? Number(calculated.emissions_tCO2e).toFixed(3) : '0.000', unit: t('common.tco2e') }}
            note={isCommuting && commutingTotalPct > 0 && commutingTotalPct < 100
              ? t('s3.underAllocated', { pct: commutingTotalPct.toFixed(0) })
              : null}
            saving={saving}
            error={flowError}
            onSave={handleFlowSave}
            saved={isSaved}
            successText={calculated
              ? t('s3.success', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period })
              : ''}
            coverage={buildCoverageChips('scope3', coverage)}
            nextStep={nextStepFor('scope3')}
            onAddAnother={handleFlowAddAnother}
            onFinish={handleFlowFinish}
            context={
              <>
                {step === 2 && docUrl && (
                  <Card title={t('elec.sourceDoc')} sub={uploadedFile ? uploadedFile.name : ''} bodyProps={{ style: { padding: 0 } }}>
                    <div style={{ borderTop: '1px solid var(--n200)', maxHeight: 420, overflow: 'auto' }}>
                      {uploadedFile && uploadedFile.type === 'application/pdf' ? (
                        <iframe title={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', height: 420, border: 0 }} />
                      ) : (
                        <img alt={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', display: 'block' }} />
                      )}
                    </div>
                  </Card>
                )}
                <CategoryContext
                  recordTypes={['scope3']}
                  categoryLabel={t('s3.title')}
                  facilities={facilities}
                  refreshKey={ledgerKey}
                  onPickSite={(id) => { handleInputChange('buildingId', String(id)); setStep(1); }}
                />
                <MonthLedger
                  period={formData.period}
                  recordTypes={['scope3']}
                  buildings={facilities}
                  refreshKey={ledgerKey}
                  emptyHint={t('s3.ledgerEmpty')}
                  onChanged={() => { fetchSummary(formData.period); setLedgerKey(k => k + 1); }}
                />
              </>
            }
          >
            <div className="k2-g2">
              <Field label={t('s3.category')} required>
                <Select value={formData.category} onChange={(e) => handleInputChange('category', e.target.value)}>
                  <option value="">{t('s3.selectCategory')}</option>
                  {SCOPE3_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </Field>

              <Field label={t('stationary.startMonth')} required>
                <Input type="month" value={formData.period} onChange={(e) => handleInputChange('period', e.target.value)} />
              </Field>
            </div>

            {categoryNotReady && <Callout tone="warn">{t('s3.notReady')}</Callout>}

            {!categoryNotReady && !isCommuting && (
              <div className="k2-g2">
                <Field label={t('s3.activity')} required>
                  <Select value={formData.activityType} onChange={(e) => handleInputChange('activityType', e.target.value)}>
                    <option value="">{t('s3.selectActivity')}</option>
                    {activityTypes.map(a => (
                      <option key={a.value || a.key} value={a.value || a.key}>{a.label}</option>
                    ))}
                  </Select>
                </Field>

                <Field label={t('stationary.quantity')} required>
                  <UnitInput
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    unit={formData.unit}
                  />
                </Field>
              </div>
            )}

            {!categoryNotReady && isCommuting && (
              <>
                <div className="k2-g2">
                  <Field label={t('s3.employees')} required>
                    <Input type="number" value={totalEmployees} onChange={(e) => setTotalEmployees(e.target.value)} />
                  </Field>
                  <Field label={t('s3.workDays')} required>
                    <Input type="number" value={workDaysPerMonth} onChange={(e) => setWorkDaysPerMonth(e.target.value)} />
                  </Field>
                </div>

                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase',
                  color: 'var(--n500)', marginTop: 4, marginBottom: 6
                }}>
                  {t('s3.survey')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--n500)', marginBottom: 10 }}>{t('s3.surveySub')}</div>

                <table className="k2-dt2" style={{ marginBottom: 10 }}>
                  <thead>
                    <tr>
                      <th>{t('s3.mode')}</th>
                      <th className="k2-r">{t('s3.share')}</th>
                      <th className="k2-r">{t('s3.avgDistance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commutingSurvey.map((row, idx) => (
                      <tr key={row.mode}>
                        <td>{row.label}</td>
                        <td className="k2-r" style={{ width: 110 }}>
                          <Input
                            type="number"
                            value={row.percentage}
                            onChange={(e) => handleSurveyChange(idx, 'percentage', e.target.value)}
                            placeholder="0"
                          />
                        </td>
                        <td className="k2-r" style={{ width: 130 }}>
                          <Input
                            type="number"
                            value={row.avgDistance}
                            onChange={(e) => handleSurveyChange(idx, 'avgDistance', e.target.value)}
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {isCommutingOverAllocated ? (
                  <Callout tone="err">{t('s3.overAllocated')}</Callout>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--n500)' }}>
                    {t('s3.allocated', { pct: commutingTotalPct.toFixed(0) })}
                  </div>
                )}
              </>
            )}
          </EntryStepper>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
